import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { price as priceUtil } from '@eti/utils';
import { Stack, Text, textVariants } from '@etg/wings';
import type { SpacingTypes } from '@etg/wings';
import { ScreenReaderPrice } from '@eti/components';
import { getAgeTypeLabel, getAgeTypeLabelForQuantity } from '../../../utils/travelers';
import { CartRow, Price } from '../../../widgets/cart/components/CartComponents';

const priceSummaryStyles = css`
  font-weight: 700;
`;

interface PriceBreakdownTravelerGroupProps {
  ageType: string;
  airlineFees: number | null;
  airlinePenalty: number | null;
  className: string;
  fareDifference: number | null;
  lineSpacing: SpacingTypes;
  quantity: number;
  serviceFee: number;
  taxDifference: number | null;
  totalDifference: number;
}

const PriceBreakdownTravelerGroup = ({
  ageType,
  airlineFees,
  airlinePenalty,
  className,
  fareDifference,
  lineSpacing,
  quantity,
  serviceFee,
  taxDifference,
  totalDifference,
}: PriceBreakdownTravelerGroupProps) => {
  const { t } = useTranslation();
  const travelerTypeLabel = getAgeTypeLabelForQuantity(ageType, quantity);
  const ageTypeLabel = t(getAgeTypeLabel(ageType)).toLowerCase();
  const travelerTypeCount = t(travelerTypeLabel, quantity);

  return (
    <Stack className={className} spacing={lineSpacing}>
      <CartRow className={priceSummaryStyles}>
        <Text
          data-testid={`changeTrip-priceBreakdown-ageTypeCount-${ageType}`}
          variant={textVariants._14_BOLD}
        >
          {travelerTypeCount}
        </Text>
      </CartRow>
      {airlinePenalty !== 0 && airlinePenalty && (
        <CartRow>
          <Text data-testid={`changeTrip-priceBreakdown-airlinePenaltyPerAgeType-${ageType}-label`}>
            {t('ChangeTrip.AirlinePenaltyPerAgeType.Label', ageTypeLabel)}
          </Text>
          <Price
            aria-hidden
            data-testid={`changeTrip-priceBreakdown-airlinePenaltyPerAgeType-${ageType}`}
          >
            {priceUtil.formatPrice(airlinePenalty)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(airlinePenalty)} />
        </CartRow>
      )}
      {fareDifference !== 0 && fareDifference && (
        <CartRow>
          <Text data-testid={`changeTrip-priceBreakdown-fareDifferencePerAgeType-${ageType}-label`}>
            {t('ChangeTrip.FareDifferencePerAgeType.Label', ageTypeLabel)}
          </Text>
          <Price
            aria-hidden
            data-testid={`changeTrip-priceBreakdown-fareDifferencePerAgeType-${ageType}`}
          >
            {priceUtil.formatPrice(fareDifference)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(fareDifference)} />
        </CartRow>
      )}
      {airlineFees !== 0 && airlineFees && (
        <CartRow>
          <Text data-testid={`changeTrip-priceBreakdown-airlineFeesPerAgeType-${ageType}-label`}>
            {t('ChangeTrip.AirlineFeesPerAgeType.Label', ageTypeLabel)}
          </Text>
          <Price
            aria-hidden
            data-testid={`changeTrip-priceBreakdown-airlineFeesPerAgeType-${ageType}`}
          >
            {priceUtil.formatPrice(airlineFees)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(airlineFees)} />
        </CartRow>
      )}
      {serviceFee !== 0 && serviceFee && (
        <CartRow>
          <Text data-testid={`changeTrip-priceBreakdown-serviceFeePerAgeType-${ageType}-label`}>
            {t('ChangeTrip.ServiceFeePerAgeType.Label', ageTypeLabel)}
          </Text>
          <Price
            aria-hidden
            data-testid={`changeTrip-priceBreakdown-serviceFeePerAgeType-${ageType}`}
          >
            {priceUtil.formatPrice(serviceFee)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(serviceFee)} />
        </CartRow>
      )}
      {taxDifference !== 0 && taxDifference && (
        <CartRow>
          <Text data-testid={`changeTrip-priceBreakdown-taxDifferencePerAgeType-${ageType}-label`}>
            {t('ChangeTrip.TaxDifferencePerAgeType.Label', ageTypeLabel)}
          </Text>
          <Price
            aria-hidden
            data-testid={`changeTrip-priceBreakdown-taxDifferencePerAgeType-${ageType}`}
          >
            {priceUtil.formatPrice(taxDifference)}
          </Price>
          <ScreenReaderPrice price={priceUtil.formatPrice(taxDifference)} />
        </CartRow>
      )}
      <CartRow className={priceSummaryStyles}>
        <Text
          data-testid={`changeTrip-priceBreakdown-totalPricePerAgeType-${ageType}-label`}
          variant={textVariants._14_BOLD}
        >
          {t('ChangeTrip.TotalPricePerAgeType.Label', travelerTypeCount)}
        </Text>
        <Price
          aria-hidden
          data-testid={`changeTrip-priceBreakdown-totalPricePerAgeType-${ageType}`}
        >
          {priceUtil.formatPrice(quantity * totalDifference)}
        </Price>
        <ScreenReaderPrice price={priceUtil.formatPrice(quantity * totalDifference)} />
      </CartRow>
    </Stack>
  );
};

export default PriceBreakdownTravelerGroup;
